import type { ButtonProps } from '@mui/material';
import { Button } from '@mui/material';
import { BILLING_CYCLE_INVOICE_TYPES, type BillingCycleInvoice } from '@vy/api-definition';
import { VyIcon } from '@vy/ui-design-system';
import { formatNumber, isNotNullOrUndefined, validateNotEmptyArray, vyDate, withDefault } from '@vy/utils';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import React, { useCallback } from 'react';
import { useBillingCycleInvoices } from '../../../hooks/requests/use-billing-cycle-invoices/use-billing-cycle-invoices';

type ExportToCsvButtonProps = {
  startDay: Date;
  endDay: Date;
} & ButtonProps;

export const ExportBillingCycleToCsv: React.FC<ExportToCsvButtonProps> = ({
  startDay,
  endDay,
  ...buttonProps
}) => {
  const regularBillingCycleInvoicesData = useBillingCycleInvoices(
    {
      pageSize: 10000,
      billingCycleInvoiceType: BILLING_CYCLE_INVOICE_TYPES.REGULAR,
    },
    {
      enabled: false,
    },
  );
  const inspectionBillingCycleInvoicesData = useBillingCycleInvoices(
    {
      pageSize: 10000,
      billingCycleInvoiceType: BILLING_CYCLE_INVOICE_TYPES.INSPECTION,
    },
    {
      enabled: false,
    },
  );

  const onExportClick = useCallback(async () => {
    const regularInvoices = await regularBillingCycleInvoicesData.refetch();
    const inspectionInvoices = await inspectionBillingCycleInvoicesData.refetch();

    handleExportCsvClick(
      [
        ...withDefault(regularInvoices.data?.invoices, []),
        ...withDefault(inspectionInvoices.data?.invoices, []),
      ],
      startDay,
      endDay,
    );
  }, [endDay, inspectionBillingCycleInvoicesData, regularBillingCycleInvoicesData, startDay]);

  return (
    <Button
      variant="outlined"
      size="small"
      {...buttonProps}
      sx={{
        columnGap: '8px',
      }}
      onClick={onExportClick}
    >
      Export
      <VyIcon
        icon="Save"
        style={{
          transform: 'scale(0.8)',
        }}
      />
    </Button>
  );
};

export function handleExportCsvClick(
  invoices: BillingCycleInvoice[] | undefined,
  startDay: Date,
  endDay: Date,
) {
  validateNotEmptyArray(invoices);
  const invoicesToExport = invoices.map(buildAggregatedInvoiceForCsv);
  const csv = Papa.unparse(invoicesToExport);
  const fileNameDayRange = formatDatesRange(startDay, endDay);

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const fileName = `Billing Cycle Report - ${fileNameDayRange}.csv`;

  saveAs(blob, fileName);
}

function formatDatesRange(startDay: Date, endDay: Date) {
  return vyDate(startDay).format('MM/DD/YYYY') + ' To ' + vyDate(endDay).format('MM/DD/YYYY');
}

function buildAggregatedInvoiceForCsv(invoice: BillingCycleInvoice) {
  return {
    Date: vyDate(invoice.billingCycle.startTime).format('MM/DD/YYYY'),
    Customer: invoice.customerName,
    ExternalID: invoice.customerExternalId,
    Amount: formatNumber(invoice.revenueBreakdown.total),
    Currency: invoice.currency,
    Contract: isNotNullOrUndefined(invoice.contractName) ? invoice.contractName : '--',
    'ERP ID': invoice.erpId,
    InvoiceNumber: invoice.invoiceNumber,
  };
}
